// ─── Build wireframe ──────────────────────────────────────────────────────────
// A first build used to open on a generic skeleton — the same three grey cards
// for a bakery and a law firm — until the model had written eighty characters
// of real markup, which on a model that thinks first was minutes. Nothing on
// screen said anything about the site being built.
//
// The workspace already knows the site's shape before a token is written: the
// approved plan lists every page and every section on it, top to bottom. So the
// preview draws that shape instead — a wireframe of THIS site, laid out the way
// each section will be (a hero, a product grid, a booking form), labelled with
// the plan's own names — and the real sections replace it as they stream in.
// With no plan, the model writes a short ===OUTLINE=== first and that is used.
//
// It lives inside the preview document, after the page's own content, under a
// root the live-preview morph never touches (see `tail` in morphChildren). It is
// drawn in custom tags (<bl-*>) so the generated stylesheet — which styles by
// TAG on purpose — cannot reach it, and in currentColor, so the moment the site's
// design system lands the frame turns into the site's own colours and type.

import { DEFAULT_PAGE, normalizePagePath } from '@/lib/sitePages';

export const WIRE_ROOT_ID = 'bl-wire';

const MAX_PAGES = 6;          // MULTI_PAGE_RULES: never more than 6
const MAX_SECTIONS = 8;
const LABEL_CHARS = 40;
// Long enough for a collapse to finish; removal is guaranteed after this even
// where transitionend never fires (a hidden frame, reduced motion).
const LEAVE_MS = 520;

// ── Structure ─────────────────────────────────────────────────────────────────

/** A section name as the model or the planner wrote it, made safe to show. */
function cleanLabel(s) {
  return String(s || '')
    .replace(/[`*_#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LABEL_CHARS);
}

/** Only a real colour may reach a CSS custom property; anything else is dropped. */
function cleanColour(value) {
  const v = String(value || '').trim();
  return /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\([^;{}]*\)|oklch\([^;{}]*\))$/i.test(v) ? v : '';
}

/** Last write per path wins, first appearance keeps its place, home leads. */
function tidyPages(pages) {
  const byPath = new Map();
  for (const p of pages) {
    if (!p?.path) continue;
    const sections = p.sections.length ? p.sections : ['Content'];
    byPath.set(p.path, { path: p.path, sections: sections.slice(0, MAX_SECTIONS) });
  }
  return [...byPath.values()]
    .sort((a, b) => (a.path === DEFAULT_PAGE ? -1 : b.path === DEFAULT_PAGE ? 1 : 0))
    .slice(0, MAX_PAGES);
}

/** The approved plan (see services/planner.js normalisePlan), as a structure. */
export function structureFromPlan(plan) {
  if (!Array.isArray(plan?.pages) || !plan.pages.length) return null;
  const pages = tidyPages(plan.pages.map((p) => ({
    path: normalizePagePath(p?.path),
    sections: (Array.isArray(p?.sections) ? p.sections : []).map(cleanLabel).filter(Boolean),
  })));
  return pages.length ? { pages, accent: cleanColour(plan?.palette?.accent) } : null;
}

/**
 * The ===OUTLINE=== block the generator writes before ===TITLE===:
 *
 *   index.html: Hero | Featured collection | Our story | Reviews
 *   shop.html: Shop intro | Product grid | Size guide
 *
 * Tolerant of what a model actually writes — a bullet in front, a dash instead
 * of a colon, commas instead of pipes. Anything that is not a page line is
 * ignored rather than guessed at.
 */
export function parseOutline(text) {
  const pages = [];
  for (const raw of String(text || '').split('\n')) {
    const m = raw.match(/^\s*(?:[-*•]\s*)?([\w./-]+?\.html?)\s*[:–—-]+\s*(.*)$/i);
    if (!m) continue;
    const body = m[2];
    const parts = body.includes('|') ? body.split('|') : body.split(/\s*[,·•]\s*/);
    pages.push({ path: normalizePagePath(m[1]), sections: parts.map(cleanLabel).filter(Boolean) });
  }
  return tidyPages(pages);
}

/**
 * What the wireframe should draw: the model's own outline once it is complete —
 * it is what will actually be written — and the approved plan until then.
 *
 * @param {object|null} plan         the plan the user approved, if any
 * @param {string}      outlineText  the ===OUTLINE=== block, '' until complete
 * @returns {{pages: Array<{path:string, sections:string[]}>, accent:string}|null}
 */
export function resolveStructure(plan, outlineText) {
  const fromPlan = structureFromPlan(plan);
  const outlined = parseOutline(outlineText);
  if (outlined.length) return { pages: outlined, accent: fromPlan?.accent || '' };
  return fromPlan;
}

/**
 * The pages to offer while a structured build runs: every planned page, each
 * with whatever has been written of it so far, then anything the model added
 * that the plan did not mention.
 */
export function withPlannedPages(pages, structure) {
  if (!structure?.pages?.length) return pages;
  const written = new Map((pages || []).map((p) => [p.path, p]));
  const planned = structure.pages.map((p) => written.get(p.path) || { path: p.path, html: '' });
  const extra = (pages || []).filter((p) => !structure.pages.some((s) => s.path === p.path));
  return [...planned, ...extra];
}

// ── Progress within a page ────────────────────────────────────────────────────

/** Tag positions, without the cost of a DOM. */
function positions(src, re) {
  const out = [];
  re.lastIndex = 0;
  let m;
  while ((m = re.exec(src)) !== null) out.push(m.index);
  return out;
}

/**
 * How many of the page's sections the stream has opened and finished.
 *
 * Counted on <section> tags, which is how the generator is told to build a page.
 * One common exception is handled: a hero written as the header itself
 * (`<header class="hero"><nav>…</nav><h1>…`) is still the page's first block, so
 * an <h1> before the first <section>, and outside the nav, counts as one — and
 * as finished once the next block begins. A logo set in an <h1> inside the nav
 * does not.
 */
export function pageProgress(html) {
  const src = String(html || '');
  const opens = positions(src, /<section\b/gi);
  const closes = positions(src, /<\/section\s*>/gi).length;
  const hasFooter = /<footer\b/i.test(src);
  const firstSection = opens.length ? opens[0] : Infinity;

  const navOpens = positions(src, /<nav\b/gi);
  const navCloses = positions(src, /<\/nav\s*>/gi);
  const insideNav = (at) =>
    navOpens.filter((i) => i < at).length > navCloses.filter((i) => i < at).length;
  const heroOutside = positions(src, /<h1\b/gi).some((at) => at < firstSection && !insideNav(at));

  return {
    started: src.trim().length > 0,
    hasFooter,
    opened: opens.length + (heroOutside ? 1 : 0),
    closed: closes + (heroOutside && (opens.length > 0 || hasFooter) ? 1 : 0),
  };
}

// ── What to draw ──────────────────────────────────────────────────────────────

// The layout a section gets, read from its name. Order matters: the first match
// wins, so the specific ("Book a fitting" is a form) comes before the general.
const KINDS = [
  ['cta',      /newsletter|subscribe|sign ?up|join us|mailing|call to action|\bcta\b|get started/i],
  // \bbook: "Book a fitting" is a form, a "Lookbook" is photographs. "Request a
  // quote" is a form too, so this comes before the testimonials' /quote/.
  ['contact',  /contact|\bbook|reserv|appointment|enquir|inquir|\bform\b|get in touch|order now|consultation|(?:get|request|free) a quote/i],
  // Where rather than how: an address, hours and a map — not a second form.
  ['location', /visit|location|find us|hours|\bmap\b|address|directions|getting here/i],
  ['quotes',   /testimonial|review|quote|what (?:people|clients|customers|guests)|press|praise|kind words/i],
  ['pricing',  /pric|\bplans\b|packages?|\brates\b|membership|tiers/i],
  ['faq',      /\bfaq|questions|q ?& ?a/i],
  ['stats',    /stats|numbers|figures|impact|results|achievements|milestones/i],
  ['gallery',  /gallery|lookbook|instagram|photos|images|moodboard/i],
  ['team',     /\bteam\b|our people|stylists|chefs|\bstaff\b|designers/i],
  ['features', /services|features|benefits|why |process|how it works|steps|values|approach|what we do|expertise|capabilities|offer/i],
  ['story',    /about|story|mission|founder|atelier|history|philosophy|craft|heritage|behind|journey|vision|who we are|\bbio\b/i],
  ['grid',     /collection|shop|product|catalog|menu|featured|work|projects|listing|propert|dishes|arrivals|bestsell|range|categor|portfolio/i],
];
const HERO_RE = /hero|banner|intro|welcome|landing|splash|masthead/i;

/** The layout a named section is drawn as. A page's first section is its hero. */
export function sectionKind(label, index = 1) {
  const s = String(label || '');
  if (HERO_RE.test(s)) return 'hero';
  for (const [kind, re] of KINDS) if (re.test(s)) return kind;
  return index === 0 ? 'hero' : 'text';
}

/**
 * The blocks still to be drawn below the page's real content.
 *
 * A page nobody has reached yet is its whole frame: nav, every section, footer.
 * The page being written sheds a block each time a section finishes, and the
 * one in progress is marked active. A page the stream has moved past draws
 * nothing — whatever it did not write, it is not going to.
 *
 * @param {{pages:Array, livePath?:string}} structure
 * @param {string} path  the page on screen
 * @param {string} html  what has been written of it so far
 * @returns {Array<{key:string, kind:string, label:string, state:string}>|null}
 *          null when this page is not part of the structure at all
 */
export function wireframeBlocks(structure, path, html) {
  const page = structure?.pages?.find((p) => p.path === path);
  if (!page) return null;

  const live = structure.livePath === path;
  const prog = pageProgress(html);
  if (prog.started && !live) return [];

  const blocks = [];
  if (!prog.started) {
    blocks.push({ key: `${path}|nav`, kind: 'nav', label: '', state: live ? 'active' : 'pending' });
  }
  if (!prog.hasFooter) {
    page.sections.forEach((label, i) => {
      if (i < prog.closed) return;
      blocks.push({
        key: `${path}|${i}|${label}`,
        kind: sectionKind(label, i),
        label,
        state: live && prog.started && i < prog.opened ? 'active' : 'pending',
      });
    });
    const allOpened = prog.opened >= page.sections.length;
    blocks.push({ key: `${path}|footer`, kind: 'footer', label: '', state: live && prog.started && allOpened ? 'active' : 'pending' });
  }
  return blocks;
}

// ── Drawing ───────────────────────────────────────────────────────────────────

const bar  = (w, s) => `<bl-b style="width:${w}%"${s ? ` data-s="${s}"` : ''}></bl-b>`;
const barP = (w, s) => `<bl-b style="width:${w}px"${s ? ` data-s="${s}"` : ''}></bl-b>`;
const box  = (h, attrs = '') => `<bl-box style="min-height:${h}"${attrs}></bl-box>`;
const pill = (accent) => `<bl-pill${accent ? ' data-acc' : ''}></bl-pill>`;
const times = (n, f) => Array.from({ length: n }, (_, i) => f(i)).join('');
const heading = (center) =>
  `<bl-stack${center ? ' data-center' : ''}>${bar(center ? 44 : 38, 'l')}${bar(center ? 30 : 26)}</bl-stack>`;

// Every template is a fixed string — nothing from the model is ever interpolated
// into markup. The one piece of model text, the label, is set as textContent.
const TEMPLATES = {
  nav: () =>
    `<bl-nav>${barP(128, 'logo')}<bl-links>${[62, 74, 58, 70].map((w) => barP(w)).join('')}</bl-links>` +
    '<bl-pill data-acc data-sm></bl-pill></bl-nav>',
  hero: () =>
    `<bl-split><bl-stack>${bar(92, 'xl')}${bar(74, 'xl')}${bar(80, 'm')}${bar(58, 'm')}` +
    `<bl-row>${pill(true)}${pill(false)}</bl-row></bl-stack>${box('clamp(240px,36vw,460px)')}</bl-split>`,
  grid: () =>
    `${heading()}<bl-grid>${times(3, () => `<bl-card>${box('clamp(170px,21vw,280px)')}${bar(72, 'm')}${bar(44)}</bl-card>`)}</bl-grid>`,
  gallery: () =>
    `${heading()}<bl-grid data-dense>${[260, 200, 300, 220, 280, 190].map((h) => box(`${h}px`)).join('')}</bl-grid>`,
  features: () =>
    `${heading()}<bl-grid>${times(3, () => `<bl-card data-pad><bl-box data-icon></bl-box>${bar(60, 'm')}${bar(92)}${bar(78)}</bl-card>`)}</bl-grid>`,
  team: () =>
    `${heading(true)}<bl-grid data-four>${times(4, () => `<bl-card data-center><bl-box data-round></bl-box>${bar(60, 'm')}${bar(40)}</bl-card>`)}</bl-grid>`,
  story: () =>
    `<bl-split data-rev>${box('clamp(230px,32vw,420px)')}<bl-stack>${bar(34, 's')}${bar(78, 'l')}` +
    `${bar(92)}${bar(96)}${bar(88)}${bar(64)}<bl-row>${pill(false)}</bl-row></bl-stack></bl-split>`,
  quotes: () =>
    `${heading(true)}<bl-grid>${times(3, () => `<bl-card data-pad>${bar(96)}${bar(90)}${bar(70)}` +
      `<bl-row><bl-box data-avatar></bl-box>${barP(96, 'm')}</bl-row></bl-card>`)}</bl-grid>`,
  contact: () =>
    `<bl-split><bl-stack>${bar(70, 'l')}${bar(90)}${bar(76)}${bar(46, 'm')}${bar(52, 'm')}</bl-stack>` +
    `<bl-card data-pad>${times(3, (i) => `<bl-field${i === 2 ? ' data-tall' : ''}></bl-field>`)}<bl-row>${pill(true)}</bl-row></bl-card></bl-split>`,
  location: () =>
    `<bl-split data-rev><bl-box data-map style="min-height:clamp(240px,30vw,380px)"></bl-box>` +
    `<bl-stack>${bar(60, 'l')}<bl-list>${[46, 58, 40].map((w) => `<bl-li>${bar(w, 'm')}${bar(w + 22)}</bl-li>`).join('')}</bl-list></bl-stack></bl-split>`,
  cta: () =>
    `<bl-band><bl-stack data-center>${bar(52, 'l')}${bar(36)}` +
    `<bl-row data-center><bl-field data-inline></bl-field>${pill(true)}</bl-row></bl-stack></bl-band>`,
  pricing: () =>
    `${heading(true)}<bl-grid>${times(3, (i) => `<bl-card data-pad${i === 1 ? ' data-hot' : ''}>${bar(40, 's')}${bar(56, 'xl')}` +
      `${times(4, () => bar(84))}<bl-row>${pill(i === 1)}</bl-row></bl-card>`)}</bl-grid>`,
  faq: () =>
    `${heading()}<bl-list>${[70, 58, 76, 64, 52].map((w) => `<bl-li>${bar(w, 'm')}</bl-li>`).join('')}</bl-list>`,
  stats: () =>
    `<bl-grid data-four>${times(4, () => `<bl-card>${bar(58, 'xl')}${bar(70)}</bl-card>`)}</bl-grid>`,
  text: () =>
    `${heading()}<bl-stack data-measure>${bar(96)}${bar(92)}${bar(97)}${bar(70)}</bl-stack>`,
  footer: () =>
    `<bl-grid data-four>${times(4, (i) => `<bl-stack>${bar(i === 0 ? 60 : 44, 'm')}${bar(70)}${bar(56)}${bar(64)}</bl-stack>`)}</bl-grid>` +
    `<bl-hr></bl-hr>${bar(30)}`,
};

function createBlock(doc, block) {
  const el = doc.createElement('bl-w');
  el.setAttribute('data-key', block.key);
  el.setAttribute('data-kind', block.kind);
  el.setAttribute('data-state', block.state);

  const inner = doc.createElement('bl-in');
  if (block.label) {
    const label = doc.createElement('bl-lbl');
    label.appendChild(doc.createElement('bl-dot'));
    const text = doc.createElement('bl-t');
    text.textContent = block.label;
    label.appendChild(text);
    inner.appendChild(label);
  }
  // Static markup from TEMPLATES only — see the note above them.
  inner.insertAdjacentHTML('beforeend', (TEMPLATES[block.kind] || TEMPLATES.text)());
  el.appendChild(inner);
  return el;
}

/**
 * Collapse a finished block rather than snapping it out, so the frame below
 * slides up to meet the section that replaced it.
 */
function leave(doc, el) {
  el.setAttribute('data-leaving', '');
  el.style.height = `${el.offsetHeight}px`;
  const view = doc.defaultView;
  const raf = view?.requestAnimationFrame?.bind(view) || ((fn) => setTimeout(fn, 16));
  raf(() => { el.style.height = '0px'; });
  setTimeout(() => el.remove(), LEAVE_MS);
}

/**
 * The site's own accent, once its stylesheet has declared one. Preferred over
 * the plan's, which is what the model was asked for rather than what it chose.
 */
function siteAccent(doc) {
  const view = doc.defaultView;
  if (!view?.getComputedStyle) return '';
  const style = view.getComputedStyle(doc.documentElement);
  for (const name of ['--accent', '--color-accent', '--accent-color', '--brand', '--primary', '--color-primary']) {
    const value = cleanColour(style.getPropertyValue(name));
    if (value) return value;
  }
  return '';
}

/** The wireframe's root, created as the last child of <body> if absent. */
export function mountWireframe(doc) {
  let root = doc.getElementById(WIRE_ROOT_ID);
  if (!root) {
    root = doc.createElement('bl-wire');
    root.id = WIRE_ROOT_ID;
    root.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(root);
  } else if (root.parentNode !== doc.body || root.nextSibling) {
    doc.body.appendChild(root);
  }
  return root;
}

/** Take the wireframe out entirely — the build ended, or this page has none. */
export function unmountWireframe(doc) {
  doc.getElementById(WIRE_ROOT_ID)?.remove();
  return null;
}

/**
 * Bring the wireframe's blocks in line with `blocks`, keyed so a block that is
 * merely changing state is updated in place — re-creating it would restart its
 * animation on every streamed chunk.
 */
export function renderWireframe(doc, root, blocks, planAccent) {
  const accent = siteAccent(doc) || planAccent || '';
  if (accent) root.style.setProperty('--bl-w-accent', accent);
  else root.style.removeProperty('--bl-w-accent');

  const wanted = new Map(blocks.map((b) => [b.key, b]));
  const present = new Map();
  for (const el of Array.from(root.children)) {
    if (el.hasAttribute('data-leaving')) continue;
    const key = el.getAttribute('data-key');
    if (wanted.has(key)) present.set(key, el);
    else leave(doc, el);
  }

  let previous = null;
  for (const block of blocks) {
    let el = present.get(block.key);
    if (!el) el = createBlock(doc, block);
    else if (el.getAttribute('data-state') !== block.state) el.setAttribute('data-state', block.state);

    // Place it directly after the previous live block, skipping any that are
    // collapsing, and only move it when it is not already there.
    let slot = previous ? previous.nextSibling : root.firstChild;
    while (slot && slot !== el && slot.hasAttribute?.('data-leaving')) slot = slot.nextSibling;
    if (slot !== el) root.insertBefore(el, slot);
    previous = el;
  }
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Everything is scoped under #bl-wire and drawn from two tokens: currentColor,
// which is whatever the site's body text is, and the accent. Before the site's
// stylesheet arrives that is quiet grey on white; after, it is the site.
// color-mix() is preceded by an rgba() fallback on every use.

export const WIREFRAME_CSS = `
#bl-wire{display:block;color:inherit;font:inherit;pointer-events:none;user-select:none;
--bl-w-accent:currentColor;
--bl-w-fill:rgba(128,128,128,.16);--bl-w-fill:color-mix(in srgb,currentColor 12%,transparent);
--bl-w-line:rgba(128,128,128,.28);--bl-w-line:color-mix(in srgb,currentColor 21%,transparent);
--bl-w-glint:rgba(255,255,255,.28);--bl-w-glint:color-mix(in srgb,currentColor 10%,transparent)}
#bl-wire *{box-sizing:border-box}
#bl-wire bl-w{display:block;position:relative;overflow:hidden;
animation:bl-w-in .55s cubic-bezier(.22,1,.36,1) backwards;
transition:opacity .35s ease,height .45s cubic-bezier(.4,0,.2,1)}
#bl-wire bl-w[data-state=pending]{opacity:.72}
#bl-wire bl-w[data-state=active]{opacity:1}
#bl-wire bl-w[data-state=active]::after{content:"";position:absolute;inset:10px;border:1.5px dashed var(--bl-w-line);border-radius:22px;pointer-events:none}
#bl-wire bl-w[data-leaving]{opacity:0}
#bl-wire bl-in{display:block;max-width:1180px;margin:0 auto;padding:clamp(40px,7vw,96px) clamp(20px,5vw,56px)}
#bl-wire bl-w[data-kind=nav] bl-in{padding-top:22px;padding-bottom:22px}
#bl-wire bl-w[data-kind=footer] bl-in{border-top:1px solid var(--bl-w-line);padding-top:clamp(40px,5vw,64px)}
#bl-wire bl-w[data-kind=hero] bl-in{min-height:min(78vh,720px);display:flex;flex-direction:column;justify-content:center}
#bl-wire bl-lbl{display:flex;align-items:center;gap:10px;margin:0 0 26px;font-size:11px;font-weight:600;line-height:1.2;letter-spacing:.16em;text-transform:uppercase;opacity:.6}
#bl-wire bl-w[data-state=active] bl-lbl{opacity:1}
#bl-wire bl-dot{display:block;flex:none;width:7px;height:7px;border-radius:50%;background:var(--bl-w-line)}
#bl-wire bl-w[data-state=active] bl-dot{background:var(--bl-w-accent);animation:bl-w-pulse 1.6s ease-in-out infinite}
#bl-wire bl-w[data-state=active] bl-t::before{content:"Writing \\00B7  "}
#bl-wire bl-b,#bl-wire bl-box,#bl-wire bl-pill,#bl-wire bl-field{display:block;position:relative;overflow:hidden;background:var(--bl-w-fill)}
#bl-wire bl-b{height:12px;margin:0 0 12px;border-radius:7px;max-width:100%}
#bl-wire bl-b[data-s=xl]{height:clamp(28px,4.2vw,50px);border-radius:12px;margin-bottom:16px}
#bl-wire bl-b[data-s=l]{height:clamp(22px,2.6vw,30px);border-radius:9px;margin-bottom:14px}
#bl-wire bl-b[data-s=m]{height:15px}
#bl-wire bl-b[data-s=s]{height:10px;margin-bottom:18px;opacity:.8}
#bl-wire bl-b[data-s=logo]{height:28px;border-radius:9px;margin:0}
#bl-wire bl-box{border-radius:16px;width:100%;min-height:180px;margin:0 0 16px}
#bl-wire bl-box[data-icon]{width:46px;min-height:46px;height:46px;border-radius:13px;margin-bottom:20px}
#bl-wire bl-box[data-round]{width:108px;min-height:108px;height:108px;border-radius:50%;margin:0 auto 18px}
#bl-wire bl-box[data-avatar]{width:38px;min-height:38px;height:38px;border-radius:50%;margin:0;flex:none}
#bl-wire bl-box[data-map]{background-color:var(--bl-w-fill);--bl-w-hair:rgba(128,128,128,.1);--bl-w-hair:color-mix(in srgb,currentColor 7%,transparent);
background-image:linear-gradient(var(--bl-w-hair) 1px,transparent 1px),linear-gradient(90deg,var(--bl-w-hair) 1px,transparent 1px);
background-size:46px 46px;margin:0}
#bl-wire bl-box[data-map]::before{content:"";position:absolute;left:50%;top:46%;width:24px;height:24px;margin:-12px 0 0 -12px;
border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:rgba(128,128,128,.5);background:color-mix(in srgb,var(--bl-w-accent) 75%,transparent)}
#bl-wire bl-pill{display:inline-block;width:138px;height:46px;border-radius:999px;flex:none}
#bl-wire bl-pill[data-sm]{width:104px;height:38px}
#bl-wire bl-pill[data-acc]{background:rgba(128,128,128,.4);background:color-mix(in srgb,var(--bl-w-accent) 70%,transparent)}
#bl-wire bl-field{height:50px;margin:0 0 14px;border-radius:12px;background:transparent;border:1.5px solid var(--bl-w-line)}
#bl-wire bl-field[data-tall]{height:118px}
#bl-wire bl-field[data-inline]{display:inline-block;width:min(320px,100%);margin:0}
#bl-wire bl-stack{display:block;min-width:0}
#bl-wire bl-stack[data-measure]{max-width:720px}
#bl-wire bl-stack[data-center],#bl-wire bl-card[data-center]{text-align:center}
#bl-wire bl-stack[data-center] bl-b,#bl-wire bl-card[data-center] bl-b{margin-left:auto;margin-right:auto}
#bl-wire bl-row{display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin-top:22px}
#bl-wire bl-row[data-center]{justify-content:center}
#bl-wire bl-row bl-b{margin:0}
#bl-wire bl-nav{display:flex;align-items:center;justify-content:space-between;gap:24px}
#bl-wire bl-links{display:flex;align-items:center;gap:28px}
#bl-wire bl-links bl-b{margin:0;height:11px}
#bl-wire bl-split{display:grid;grid-template-columns:1.1fr .9fr;gap:clamp(24px,5vw,72px);align-items:center}
#bl-wire bl-split[data-rev]{grid-template-columns:.9fr 1.1fr}
#bl-wire bl-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:clamp(16px,2.6vw,30px);margin-top:30px}
#bl-wire bl-grid[data-four]{grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-top:0}
#bl-wire bl-grid[data-dense]{grid-template-columns:repeat(auto-fit,minmax(180px,1fr));align-items:start}
#bl-wire bl-card{display:block;min-width:0}
#bl-wire bl-card[data-pad]{padding:26px;border:1px solid var(--bl-w-line);border-radius:20px}
#bl-wire bl-card[data-hot]{border-color:var(--bl-w-accent)}
#bl-wire bl-band{display:block;padding:clamp(40px,6vw,76px) clamp(20px,4vw,48px);border-radius:28px;background:var(--bl-w-fill)}
#bl-wire bl-list{display:block;margin-top:24px;max-width:820px}
#bl-wire bl-li{display:block;padding:22px 0;border-bottom:1px solid var(--bl-w-line)}
#bl-wire bl-li bl-b{margin:0}
#bl-wire bl-li bl-b+bl-b{margin-top:10px}
#bl-wire bl-hr{display:block;height:1px;background:var(--bl-w-line);margin:40px 0 20px}
#bl-wire bl-w[data-state=active] bl-b::after,#bl-wire bl-w[data-state=active] bl-box::after,
#bl-wire bl-w[data-state=active] bl-pill::after{content:"";position:absolute;inset:0;transform:translateX(-100%);
background:linear-gradient(90deg,transparent,var(--bl-w-glint),transparent);animation:bl-w-shine 1.7s ease-in-out infinite}
@keyframes bl-w-in{from{opacity:0;transform:translateY(16px)}}
@keyframes bl-w-shine{to{transform:translateX(100%)}}
@keyframes bl-w-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(.7)}}
@media(max-width:760px){
#bl-wire bl-split,#bl-wire bl-split[data-rev]{grid-template-columns:1fr}
#bl-wire bl-links{display:none}
#bl-wire bl-w[data-kind=hero] bl-in{min-height:0}}
@media(prefers-reduced-motion:reduce){
#bl-wire bl-w,#bl-wire bl-dot,#bl-wire bl-b::after,#bl-wire bl-box::after,#bl-wire bl-pill::after{animation:none!important}
#bl-wire bl-w{transition:none}}
`.replace(/\n/g, '');
